import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useE2E } from '../context/E2EContext';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Logo from '../components/Logo';

export default function Login() {
  const { login } = useAuth();
  const e2e = useE2E();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { tmp_token }
  const [code, setCode] = useState('');

  const initE2EAndRedirect = async (isAdmin) => {
    try {
      if (e2e?.supported && password) {
        await e2e.initialize(password);
      }
    } catch (e) {
      console.warn('[E2E init failed]', e);
    }
    window.location.href = isAdmin ? '/admin' : '/home';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoading(true);
    try {
      const { data } = await (await import('../lib/api')).default.post('/auth/login', { username: username.trim(), password });
      if (data.requires_2fa) {
        setTwoFA({ tmp_token: data.tmp_token });
        toast.info('Inserisci il codice 2FA');
        return;
      }
      localStorage.setItem('gtc_token', data.token);
      toast.success('Accesso riuscito');
      await initE2EAndRedirect(data.user.is_admin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Credenziali non valide');
    } finally { setLoading(false); }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await (await import('../lib/api')).default.post('/auth/2fa/verify', { tmp_token: twoFA.tmp_token, code: code.trim() });
      localStorage.setItem('gtc_token', data.token);
      toast.success('Accesso completato');
      await initE2EAndRedirect(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Codice non valido');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 safe-top safe-bottom" data-testid="login-page">
      {/* background glow */}
      <div className="absolute inset-0 -z-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.15)_0%,transparent_60%)]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-sm"
      >
        <div className="flex flex-col items-center mb-10">
          <div className="mb-5 gold-glow">
            <Logo size={80} />
          </div>
          <h1 className="font-heading text-4xl font-light tracking-tight text-white">
            Golden <span className="text-[var(--gold)] font-medium">TV</span>
          </h1>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mt-2">Connect</p>
        </div>

        <form onSubmit={twoFA ? verify2FA : submit} className="space-y-4">
          {!twoFA ? (
            <>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Username</label>
                <input
                  data-testid="login-username-input"
                  type="text" autoComplete="username" value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input-gold" placeholder="il_tuo_username" required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Password</label>
                <input
                  data-testid="login-password-input"
                  type="password" autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-gold" placeholder="••••••••" required
                />
              </div>
            </>
          ) : (
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Codice 2FA</label>
              <input
                data-testid="login-2fa-code"
                type="text" inputMode="numeric" maxLength={10}
                value={code} onChange={(e) => setCode(e.target.value)}
                className="input-gold text-center tracking-[0.4em] text-lg"
                placeholder="123456" required autoFocus
              />
              <p className="text-[10px] text-white/40 mt-2 text-center">Inserisci il codice dall'app authenticator o un codice di recupero.</p>
            </div>
          )}
          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-gold w-full py-4 flex items-center justify-center"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (twoFA ? 'Verifica' : 'Accedi')}
          </button>
          {twoFA && (
            <button type="button" onClick={() => { setTwoFA(null); setCode(''); }} className="w-full py-2 text-xs text-white/50 hover:text-white">
              ← Annulla
            </button>
          )}
        </form>

        <p className="mt-8 text-xs text-center text-white/40 leading-relaxed">
          {"L'accesso è consentito solo con credenziali fornite dall'amministratore."}
          <br />
          Nessuna registrazione richiesta.
        </p>
      </motion.div>
    </div>
  );
}
